import * as React from "react";
import { observer } from "mobx-react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { Box, Typography, MenuItem, ListItemIcon } from "@mui/material";
import { CrudRows, CrudRow, FooterData, GridColumn } from "../types";
import { IMenuAction } from "../../../common/controls";
import { FileIcon } from "../../../common/components/FileIcon";

type ShowFilesCompactProps = {
  rows: CrudRows;
  columns: GridColumn[];
  menuActions?: IMenuAction[];
  label: string;
  showLabel?: boolean;
  footerData?: FooterData;
  hidden?: boolean;
};

export const ShowFilesCompact: React.FC<ShowFilesCompactProps> = observer(
  ({
    rows,
    columns,
    menuActions = [],
    label,
    showLabel = true,
    footerData,
    hidden = false,
  }) => {
    if (hidden) return null;
    const gridRows = React.useMemo(() => {
      return Object.values(rows) as CrudRow[];
    }, [rows]);

    const mrtColumns = React.useMemo(() => {
      const dataColumns = columns.filter(
        (col) => !col.name.startsWith("__action")
      );
      return dataColumns.map((col) => {
        const footerCell = footerData?.[col.name];

        if (col.name === "mimetype") {
          return {
            accessorKey: col.name,
            header: col.title || col.name,
            size: 60,
            Footer: footerCell
              ? () => (
                  <Box sx={{ fontWeight: 500, ...footerCell.sx }}>
                    {footerCell.value}
                  </Box>
                )
              : undefined,
            Cell: ({ row }: any) => (
              <FileIcon
                filename={row.original.name}
                mimetype={row.original.mimetype}
                size={32}
              />
            ),
          };
        }
        return {
          accessorKey: col.name,
          header: col.title || col.name,
          size: col.name === "formattedSize" ? 80 : 300,
          grow: col.name === "name",
          Footer: footerCell
            ? () => (
                <Box sx={{ fontWeight: 500, ...footerCell.sx }}>
                  {footerCell.value}
                </Box>
              )
            : undefined,
        };
      }) as MRT_ColumnDef<any>[];
    }, [columns, footerData]);

    const hasMenuActions = menuActions.length > 0;

    return (
      <Box>
        {showLabel && (
          <Box sx={{ marginLeft: "10px", marginBottom: "8px" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
              {`${label}:`}
            </Typography>
          </Box>
        )}

        <MaterialReactTable
          layoutMode="grid"
          columns={mrtColumns}
          data={gridRows}
          enableEditing={false}
          enableRowActions={hasMenuActions}
          positionActionsColumn="first"
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={false}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          enableGlobalFilter={false}
          enableDensityToggle={false}
          initialState={{
            density: "compact",
          }}
          displayColumnDefOptions={
            hasMenuActions
              ? {
                  "mrt-row-actions": {
                    header: "",
                    size: 50,
                    minSize: 50,
                    maxSize: 50,
                    grow: false,
                  },
                }
              : undefined
          }
          muiTableHeadCellProps={{
            sx: {
              verticalAlign: "middle",
            },
          }}
          renderRowActionMenuItems={
            hasMenuActions
              ? ({ row, closeMenu }) => {
                  const rowData = row.original;

                  return menuActions.map((action, index) => {
                    const isEnabled = action.isActionEnabled
                      ? action.isActionEnabled(rowData)
                      : true;

                    return (
                      <MenuItem
                        key={`action-${index}`}
                        disabled={!isEnabled}
                        onClick={(e) => {
                          action.onClick(rowData, e);
                          closeMenu();
                        }}
                      >
                        <ListItemIcon>{action.icon}</ListItemIcon>
                        {action.label}
                      </MenuItem>
                    );
                  });
                }
              : undefined
          }
        />
      </Box>
    );
  }
);
